import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Download, Upload, FileSpreadsheet, Search, Star } from "lucide-react";
import { toast } from "sonner";

const empty = {
  code: "", name: "",
  contact_person: "", phone: "", whatsapp: "", email: "",
  gstin: "", pan: "",
  address: "", city: "", state: "", country: "India", pin: "",
  payment_terms: "", lead_time_days: 0, material_categories: "",
  bank_name: "", bank_account: "", ifsc: "",
  status: "active", rating: 0, notes: "",
};

const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  blacklisted: "bg-red-50 text-red-700",
};

export default function SuppliersPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all");
  const [busy, setBusy] = useState(false);

  const load = async () => setList((await api.get("/suppliers")).data);
  useEffect(() => { load(); }, []);

  const startCreate = () => { setForm(empty); setEditing(null); setOpen(true); };
  const startEdit = (s) => { setForm({ ...empty, ...s }); setEditing(s.id); setOpen(true); };

  const save = async () => {
    if (busy) return;
    if (!form.code || !form.name) return toast.error("Code and Name are required");
    setBusy(true);
    try {
      if (editing) await api.put(`/suppliers/${editing}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Supplier updated" : "Supplier created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier ${s.code}?`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Supplier deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/suppliers/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const msg = `Imported ${file.name} · created ${data.created}, updated ${data.updated}` +
        (data.errors?.length ? ` · ${data.errors.length} row error(s)` : "");
      if (data.errors?.length) toast.warning(msg, { description: data.errors.slice(0, 3).join(" | ") });
      else toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    }
  };

  const filtered = list.filter((s) => {
    if (statusFilter !== "__all" && s.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [s.code, s.name, s.gstin, s.city, s.state, s.contact_person, s.email]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Procurement</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Supplier Master
          </h1>
          <p className="text-sm text-slate-500 mt-1">Central vendor list — used by GIN, Purchase Requests, and Purchase Orders. Bulk import/export supported.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadCsv("/suppliers/template-csv", "suppliers_template.csv")} data-testid="sup-csv-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={() => downloadCsv("/suppliers/export-csv", "suppliers_export.csv")} data-testid="sup-csv-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <label className="inline-flex items-center h-10 rounded-md border border-slate-200 px-3 text-sm cursor-pointer hover:bg-slate-50">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} data-testid="sup-csv-import-input" />
          </label>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={startCreate} data-testid="new-supplier-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Supplier
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
              <SheetHeader><SheetTitle data-testid="sup-sheet-title">{editing ? "Edit Supplier" : "New Supplier"}</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-6">
                <section>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Identity</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SUP-001" data-testid="sup-code-input" /></div>
                    <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name-input" /></div>
                    <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="29AAACH1234F1Z5" /></div>
                    <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} placeholder="AAACH1234F" /></div>
                    <div>
                      <Label>Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="blacklisted">Blacklisted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Rating (0–5)</Label><Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseFloat(e.target.value || 0) })} /></div>
                  </div>
                </section>

                <section>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Contact</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                    <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" /></div>
                    <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div>
                      <Label title="Auto-MRP purchase orders and follow-ups are sent here on WhatsApp (falls back to Phone)">WhatsApp No.</Label>
                      <Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91 …" data-testid="supplier-whatsapp-input" />
                    </div>
                  </div>
                </section>

                <section>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Address</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                    <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                    <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                    <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                    <div><Label>PIN</Label><Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} /></div>
                  </div>
                </section>

                <section>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Commercial</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Net 30" /></div>
                    <div><Label>Lead Time (days)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: parseInt(e.target.value || 0, 10) })} /></div>
                    <div><Label>Material Categories</Label><Input value={form.material_categories} onChange={(e) => setForm({ ...form, material_categories: e.target.value })} placeholder="AL_PIPE,AL_ROD" /></div>
                  </div>
                </section>

                <section>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Banking (optional)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
                    <div><Label>Account No</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></div>
                    <div><Label>IFSC</Label><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} /></div>
                  </div>
                </section>

                <section>
                  <Label>Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </section>
              </div>
              <SheetFooter className="mt-6">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-supplier-btn">{editing ? "Update" : "Create"}</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by code, name, GSTIN, contact..." className="pl-9" data-testid="sup-search-input" />
        </div>
        <div className="w-52">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="sup-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="blacklisted">Blacklisted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 mono">{filtered.length}/{list.length} suppliers</div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="suppliers-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-2.5 px-4">Code</th>
              <th className="py-2.5 px-4">Name</th>
              <th className="py-2.5 px-4">GSTIN</th>
              <th className="py-2.5 px-4">City / State</th>
              <th className="py-2.5 px-4">Payment / Lead</th>
              <th className="py-2.5 px-4">Rating</th>
              <th className="py-2.5 px-4">Status</th>
              <th className="py-2.5 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-sm text-slate-500">No suppliers match — try importing a CSV or clicking <span className="mono">New Supplier</span>.</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`sup-row-${s.code}`}>
                <td className="py-3 px-4 mono font-semibold text-slate-800">{s.code}</td>
                <td className="py-3 px-4 text-slate-700">
                  {s.name}
                  {s.contact_person && <div className="text-xs text-slate-500">{s.contact_person}{s.phone ? ` · ${s.phone}` : ""}</div>}
                </td>
                <td className="py-3 px-4 mono text-xs text-slate-600">{s.gstin || "—"}</td>
                <td className="py-3 px-4 text-xs text-slate-600">{s.city}{s.state ? `, ${s.state}` : ""}</td>
                <td className="py-3 px-4 text-xs text-slate-600">
                  {s.payment_terms || "—"}{s.lead_time_days ? ` · ${s.lead_time_days}d` : ""}
                </td>
                <td className="py-3 px-4 text-xs">
                  {s.rating > 0 ? (
                    <span className="inline-flex items-center gap-0.5 mono text-amber-700 font-semibold">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {s.rating.toFixed(1)}
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONE[s.status] || "bg-slate-100"}`} data-testid={`sup-status-${s.code}`}>{s.status}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`edit-sup-${s.code}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-sup-${s.code}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
